import { Separator, Stack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { CodingHeader } from "./shared/CodingHeader";
import { NodeView } from "./NodeView/NodeView";
import { SpreadsheetView } from "./SpreadsheetView/SpreadsheetView";
import { CodingView } from "../../../../coding/models/CodingView";
import { LoadingScreen } from "../../../../shared/views/LoadingScreen/LoadingScreen";
import { useAutoUpdateQueryParam } from "../../hooks/useAutoUpdateQueryParam";
import { useStores } from "../../../../shared/stores/RootStore";
import { MapMode } from "../../../../survey/stores/MapStore";

const CodingPageBase: React.FC = () => {
  const { codingStore, craWayStore, craNodeStore, craCommentStore, mapStore } =
    useStores();

  const [initialLoadingDone, setInitialLoadingDone] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    codingStore.setView(
      CodingView.fromString(searchParams.get(CodingView.param) ?? ""),
    );
    mapStore.setMode(MapMode.CraCoding);
    return () => {
      mapStore.setMode(MapMode.SurveyPage);
    };
    //eslint-disable-next-line
  }, []);

  useAutoUpdateQueryParam(CodingView.param, codingStore.view, setSearchParams);

  const selectedCraWay = craWayStore.getSelected();
  const selectedNode = craNodeStore.getSelected();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!codingStore.changesSaved) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      codingStore.stopAutoSaveTimer();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setInitialLoadingDone(false);
    codingStore.resetState();

    const canCode = !!selectedCraWay?.permissions.canCode;

    const onCraWayChange = async (craWayId: string) => {
      await codingStore.fetchAttributesByCraWayId(craWayId);

      if (canCode) {
        await Promise.all([
          codingStore.fetchLatestCodingChange(craWayId),
          craCommentStore.fetchComments(craWayId),
        ]);

        codingStore.startAutoSaveTimer();
      }

      setInitialLoadingDone(true);
    };

    if (selectedCraWay) {
      onCraWayChange(selectedCraWay.id);
    }

    return () => {
      if (canCode && !codingStore.changesSaved) {
        codingStore.onUnloadSaveChanges();
      }
    };
    // eslint-disable-next-line
  }, [selectedCraWay?.id]);

  useEffect(() => {
    if (selectedNode && initialLoadingDone) {
      codingStore.suggestionEngine.suggestGradeType(
        selectedNode.id,
        selectedNode.slope,
      );

      if (codingStore.view !== CodingView.Enum.Spreadsheet) {
        codingStore.copyFromPreviousNode();
      }
    }

    //eslint-disable-next-line
  }, [selectedNode, initialLoadingDone]);

  useEffect(() => {
    const refreshWay = async () => {
      if (!selectedNode && selectedCraWay?.id) {
        await craWayStore.refreshCraWay(selectedCraWay.id);
      }
    };
    refreshWay();
    // eslint-disable-next-line
  }, [selectedNode]);

  return (
    <Stack
      flexGrow={1}
      overflow="auto"
      position={"relative"}
      separator={<Separator alignSelf={"stretch"} />}
    >
      {initialLoadingDone ? (
        <>
          <CodingHeader />
          <Separator />

          {codingStore.view === CodingView.Enum.Spreadsheet ? (
            <SpreadsheetView />
          ) : (
            <NodeView />
          )}
        </>
      ) : (
        <LoadingScreen />
      )}
    </Stack>
  );
};

export const CodingPage = observer(CodingPageBase);
